import * as React from 'react';
import { useSelector } from 'react-redux';

import { ReduxState } from 'src/store/types';

export const useReduxSelector = <T>(
  selector: (state: ReduxState) => T,
  equalityFn?: (prev: T, curr: T) => boolean
) => {
  const current = useSelector<ReduxState, T>(selector, equalityFn);

  // When supplying an equalityFn, useSelector does not return the
  // previous state's object when the next state is deemed equal:
  // https://github.com/reduxjs/react-redux/pull/1660
  //
  // This is a problem when comparing deeply equal values since their
  // shallow comparison may be unequal and cause an unnecessary re-render.
  //
  // Therefore, when an equalityFn is provided, we use React state to
  // to always provide the last selected state if next value is equal.
  const [state, setState] = React.useState(current);
  React.useEffect(() => {
    if (equalityFn && !equalityFn(state, current)) {
      setState(current);
    }
  }, [current, equalityFn, state]);

  return equalityFn ? state : current;
};
